import { DomainSchema } from '@multitec/domain'
import { useQuery } from 'react-query'
import { z } from 'zod'

import { Base } from '../base'
import { MyURLSearchParams } from '../utils/url-search-params'

class Create extends Base {
  private body = z.object({
    equipmentId: z.string(),
    name: z.string(),
    imageStr: z.instanceof(File).optional(),
  })

  private response = z.object({
    id: z.string(),
  })

  handle = async (body: z.infer<typeof this.body>) => {
    const formData = new FormData()
    formData.append('equipmentId', body.equipmentId)
    formData.append('name', body.name)
    body.imageStr && formData.append('imageStr', body.imageStr)

    const { data } = await this.axios.post('/entity/vistas/', formData)
    return this.response.parse(data.result)
  }
}

class Edit extends Base {
  private body = z.object({
    name: z.string(),
    imageStr: z.instanceof(File).optional(),
  })

  private response = z.object({
    id: z.string(),
  })

  handle = async (id: string, body: z.infer<typeof this.body>) => {
    const formData = new FormData()
    formData.append('name', body.name)
    body.imageStr && formData.append('imageStr', body.imageStr)

    const { data } = await this.axios.put('/entity/vistas/' + id, formData)
    return this.response.parse(data.result)
  }
}

class Delete extends Base {
  handle = async (id: string) => {
    await this.axios.delete('/entity/vistas/' + id)
  }
}

class LoadByEquipment extends Base {
  private response = z.array(DomainSchema.EquipmentVista)

  handle = (equipment_id?: string | number | null) => {
    return useQuery(
      ['equipment-vista', 'load-by-equipment', { equipment_id }],
      async () => {
        if (!equipment_id) return []

        const queryParams = new MyURLSearchParams({
          equipmentId: equipment_id,
        }).toString()
        const { data } = await this.axios.get('/entity/vistas?' + queryParams)
        return this.response.parse(data.result)
      },
    )
  }
}

class LoadOne extends Base {
  private response = DomainSchema.EquipmentVista

  handle = (id: string) => {
    return useQuery(['equipment-vista', 'load-one', { id }], async () => {
      const { data } = await this.axios.get('/entity/vistas/' + id)
      return this.response.parse(data.result)
    })
  }
}

class SetMyItems extends Base {
  private body = z.object({
    items: z.array(
      z.object({
        product_id: z.string(),
      }),
    ),
  })

  private response = z.object({
    id: z.string(),
  })

  handle = async (id: string, body: z.infer<typeof this.body>) => {
    const { data } = await this.axios.post(`/entity/vistas/${id}/items`, body)
    return this.response.parse(data.result)
  }
}

export default class extends Base {
  Create = new Create(this.axios).handle
  Edit = new Edit(this.axios).handle
  LoadByEquipment = new LoadByEquipment(this.axios).handle
  LoadOne = new LoadOne(this.axios).handle
  Delete = new Delete(this.axios).handle
  SetMyItems = new SetMyItems(this.axios).handle
}
